import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { CountdownBadge } from '@/components/extraction/CountdownBadge'
import { KeyDateEditPopover } from '@/components/extraction/KeyDateEditPopover'
import { layoutKeyDatesTimeline, type TimelineMarker } from '@/lib/key-dates-timeline'
import { KEY_DATE_PHASE_COLORS, KEY_DATE_PHASE_LABELS } from '@/types/extraction'
import type { ExtractedDate } from '@/types/extraction'
import { cn } from '@/lib/utils'

// ===================
// Types
// ===================

interface KeyDatesTimelineViewProps {
  bidId: string
  items: ExtractedDate[]
  readOnly: boolean
}

// ===================
// Layout constants
// ===================

/**
 * Markers are absolutely positioned, so they add no height to their lane and a
 * tall one would spill into the lane below. Two rules keep lanes apart:
 *
 * 1. Every marker's height is bounded — the event label is clamped to two lines
 *    (the full text stays reachable through `title`, as the tables do it).
 * 2. Each lane is sized to the tallest marker it can hold. A marker is a dot,
 *    up to two label lines and a date line; a submission deadline adds a
 *    countdown line and a hand-edited date adds a "set manually" line.
 */
const LANE_SIZE_CLASS = {
  base: 'min-h-[5rem]',
  tall: 'min-h-[6.25rem]',
  taller: 'min-h-[7.5rem]',
} as const

type LaneSize = keyof typeof LANE_SIZE_CLASS

/** Within how many percentage points of an end the Today label must stop centring. */
const TODAY_EDGE_PCT = 6

const TODAY_ALIGN_CLASS = {
  start: 'translate-x-0',
  center: '-translate-x-1/2',
  end: '-translate-x-full',
} as const

type TodayAlign = keyof typeof TODAY_ALIGN_CLASS

// ===================
// Helpers
// ===================

/** The tallest marker a lane can hold decides the lane's height. */
function laneSizeFor(laneMarkers: TimelineMarker[]): LaneSize {
  const extraLines =
    (laneMarkers.some((m) => m.item.isSubmissionDeadline) ? 1 : 0) +
    (laneMarkers.some((m) => m.item.userTouched) ? 1 : 0)
  if (extraLines >= 2) return 'taller'
  if (extraLines === 1) return 'tall'
  return 'base'
}

/**
 * The line stays exactly on `todayPct`; only its label shifts, so a Today that
 * lands at either end of the axis still reads inside the block.
 */
function todayAlignFor(todayPct: number): TodayAlign {
  if (todayPct <= TODAY_EDGE_PCT) return 'start'
  if (todayPct >= 100 - TODAY_EDGE_PCT) return 'end'
  return 'center'
}

function formatAxisLabel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })
}

function formatMarkerDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

function phasePillClass(item: ExtractedDate): string {
  return KEY_DATE_PHASE_COLORS[item.phase ?? 'tender']
}

function phaseLabel(item: ExtractedDate): string {
  return KEY_DATE_PHASE_LABELS[item.phase ?? 'tender']
}

// ===================
// Marker
// ===================

function MarkerBody({ marker }: { marker: TimelineMarker }) {
  const { item } = marker
  return (
    <span className="flex w-28 flex-col items-center gap-1 text-center">
      <span
        className={cn(
          'rounded-full border border-border',
          phasePillClass(item),
          item.isSubmissionDeadline ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5'
        )}
        aria-hidden="true"
      />
      <span
        data-marker-label
        title={item.event}
        className={cn(
          'line-clamp-2 w-full text-xs leading-tight',
          item.isSubmissionDeadline && 'font-medium'
        )}
      >
        {item.event}
      </span>
      <span className="text-meta leading-tight">{formatMarkerDate(item.dateIso)}</span>
      {item.isSubmissionDeadline && <CountdownBadge dateIso={item.dateIso} />}
      {item.userTouched && <span className="text-meta leading-tight">set manually</span>}
    </span>
  )
}

// ===================
// Component
// ===================

/**
 * The key dates as a horizontal schedule rather than a table.
 *
 * Everything positional comes from `layoutKeyDatesTimeline`, which is pure and
 * lane-assigns the markers; this component only turns lanes into rows. Long
 * tenders stack a lot of monthly milestones, so the block grows downward — it
 * never packs two markers into one row. Lane heights come from `LANE_SIZE_CLASS`
 * rather than from flow, because the markers inside a lane are absolutely
 * positioned and would otherwise spill into the lane below.
 *
 * Only absolute, parseable dates can be placed. Relative wording ("10 business
 * days after award") and blank dates land in the tray below, where they are
 * listed rather than hidden: an undated milestone is still a milestone, and
 * dropping it would make the timeline quietly incomplete.
 */
export function KeyDatesTimelineView({
  bidId,
  items,
  readOnly,
}: KeyDatesTimelineViewProps) {
  const layout = useMemo(() => layoutKeyDatesTimeline(items, new Date()), [items])

  const laneCount = layout.markers.reduce((max, m) => Math.max(max, m.lane + 1), 0)
  const lanes = useMemo(
    () =>
      Array.from({ length: laneCount }, (_, lane) => {
        const laneMarkers = layout.markers.filter((m) => m.lane === lane)
        return { markers: laneMarkers, size: laneSizeFor(laneMarkers) }
      }),
    [layout.markers, laneCount]
  )

  const todayAlign = todayAlignFor(layout.todayPct)

  return (
    <div className="space-y-4">
      {layout.markers.length > 0 ? (
        <div
          role="group"
          aria-label="Key dates timeline"
          className="rounded-xl border border-border bg-card p-4 shadow-sm"
        >
          <div className="flex items-center justify-between text-meta">
            <span>{formatAxisLabel(layout.axisStartIso)}</span>
            <span>{formatAxisLabel(layout.axisEndIso)}</span>
          </div>

          <div className="relative mt-2">
            {/* Today: one line down the whole block, so a marker's position
                reads as before/after now without counting off the axis. */}
            <div
              className="absolute inset-y-0 w-px bg-[hsl(var(--brand))]"
              style={{ left: `${layout.todayPct}%` }}
              aria-hidden="true"
            />
            <span
              data-today-align={todayAlign}
              className={cn(
                'absolute top-0 text-xs font-medium text-[hsl(var(--brand))]',
                TODAY_ALIGN_CLASS[todayAlign]
              )}
              style={{ left: `${layout.todayPct}%` }}
            >
              Today
            </span>

            <div className="mt-6 h-px bg-border" />

            <div className="pt-2">
              {lanes.map(({ markers: laneMarkers, size }, lane) => (
                <div
                  key={lane}
                  data-lane={lane}
                  data-lane-size={size}
                  className={cn('relative', LANE_SIZE_CLASS[size])}
                >
                  {laneMarkers.map((marker) => (
                    <div
                      key={marker.id}
                      data-marker
                      className="absolute top-0 -translate-x-1/2"
                      style={{ left: `${marker.leftPct}%` }}
                    >
                      {readOnly ? (
                        <MarkerBody marker={marker} />
                      ) : (
                        <KeyDateEditPopover
                          item={marker.item}
                          bidId={bidId}
                          trigger={
                            <button
                              type="button"
                              className="rounded-lg p-1 transition-colors hover:bg-accent"
                            >
                              <MarkerBody marker={marker} />
                            </button>
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-meta">No dated events yet.</p>
      )}

      {layout.unplaced.length > 0 && (
        <section aria-label="No fixed date">
          <h4 className="text-sm font-medium">No fixed date</h4>
          <div className="mt-2 divide-y divide-border rounded-xl border border-border bg-card shadow-sm">
            {layout.unplaced.map((item, index) => (
              <div
                key={item.id ?? `unplaced-${index}`}
                className="flex flex-wrap items-center gap-3 p-3"
              >
                <span className="min-w-0 flex-1 text-sm">{item.event}</span>
                <span
                  className={cn(
                    'inline-flex items-center rounded-lg px-2 py-0.5 text-xs',
                    phasePillClass(item)
                  )}
                >
                  {phaseLabel(item)}
                </span>
                <span className="text-meta italic">
                  {item.dateText.trim() || 'No explicit date captured'}
                </span>
                {!readOnly && item.id && (
                  <KeyDateEditPopover
                    item={item}
                    bidId={bidId}
                    trigger={
                      <Button variant="outline" size="sm">
                        Set date
                      </Button>
                    }
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
